import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Dialog, Grid, TextField, MenuItem, Button, Stack, Typography, FormControlLabel, Checkbox } from '@mui/material';
import InputMask from 'react-input-mask';
import api from '../../services/api';

export default function ModalAddAddress({ modalStatus, closeModelFunction, addNewAddressFunction }) {
    const [customUFs, setCustomUFs] = useState([]);
    const [defaultUFs, setDefaultUFs] = useState([]);
    const [isUFsLoading, setIsUFsLoading] = useState(true);
    const [idDM001, setIdDM001] = useState(null); // Certifique-se de que idDM001 está definido corretamente

    useEffect(() => {
        const lsClient = JSON.parse(localStorage.getItem('userDataLogin'))
        setIdDM001(lsClient.ID_DM001)
    }, []);

    const schema = Yup.object().shape({
        NM_ENDERECO: Yup.string().min(3, 'Mínimo 3 caracteres').max(50, 'Máximo 50 caracteres').required('Nome do Endereço é obrigatório'),
        NR_CEP: Yup.string().matches(/^[0-9]{5}-[0-9]{3}$/, 'CEP inválido').required('CEP é obrigatório'),
        DS_LOGRADOURO: Yup.string().min(3, 'Mínimo 3 caracteres').max(150, 'Máximo 150 caracteres').required('Rua é obrigatória'),
        NR_PREDIO: Yup.string().max(20, 'Máximo 20 caracteres').required('Número do Prédio é obrigatório'),
        NM_BAIRRO: Yup.string().min(3, 'Mínimo 3 caracteres').max(50, 'Máximo 50 caracteres').required('Bairro é obrigatório'),
        NM_CIDADE: Yup.string().min(3, 'Mínimo 3 caracteres').max(50, 'Máximo 50 caracteres').required('Cidade é obrigatória'),
        NM_UF: Yup.string().max(30, 'Máximo 30 caracteres').required('UF é obrigatório'),
        DS_COMPLEMENTO: Yup.string().max(100, 'Máximo 100 caracteres'),
        DS_REFERENCIA: Yup.string().max(150, 'Máximo 150 caracteres'),
        IN_PADRAO: Yup.boolean(),
    });

    const { register, handleSubmit, formState: { errors }, control, setValue, reset } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            NM_ENDERECO: '',
            NR_CEP: '',
            DS_LOGRADOURO: '',
            NR_PREDIO: '',
            NM_BAIRRO: '',
            NM_CIDADE: '',
            NM_UF: '',
            DS_COMPLEMENTO: '',
            DS_REFERENCIA: '',
            IN_PADRAO: false,
        },
    });

    useEffect(() => {
        const fetchUFs = async () => {
            try {
                const defaultParams = { DS_KEY_PARAMETRO: 'DEFAULT_UF' };
                const defaultResponse = await api.post('filter_parms', defaultParams);
                setDefaultUFs((defaultResponse.data[0]?.DS_VALOR_PARAMETRO || '').split(';'));

                const customParams = { DS_KEY_PARAMETRO: 'CUSTOM_UF' };
                const customResponse = await api.post('filter_parms', customParams);
                setCustomUFs((customResponse.data[0]?.DS_VALOR_PARAMETRO || '').split(';'));
            } catch (error) {
                console.error('Error fetching UFs:', error);
            } finally {
                setIsUFsLoading(false);
            }
        };

        fetchUFs();
    }, []);

    const removeZipCodeMask = (value) => {
        return value.replace(/\D/g, '');
    };

    const onSubmit = (data) => {
        const dataAddressRegister = {
            ID_DM001: idDM001, // Certifique-se de que idDM001 está definido no estado ou contexto
            TP_ENDERECO: "1",
            NM_ENDERECO: data.NM_ENDERECO,
            NR_CEP: removeZipCodeMask(data.NR_CEP),
            DS_LOGRADOURO: data.DS_LOGRADOURO,
            NR_PREDIO: data.NR_PREDIO,
            NM_BAIRRO: data.NM_BAIRRO,
            DS_COMPLEMENTO: data.DS_COMPLEMENTO || "N/A",
            NM_CIDADE: data.NM_CIDADE,
            DS_REFERENCIA: data.DS_REFERENCIA || "N/A",
            IN_PADRAO: data.IN_PADRAO ? "S" : "N",
            NM_CONTATO: data.NM_CONTATO || "N/A", // Adicionei NM_CONTATO como opcional
            IN_SITUACAO: "A",
            NM_UF: data.NM_UF,
        };
        addNewAddressFunction(dataAddressRegister);
        closeModelFunction();
        reset();
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={modalStatus} onClose={closeModelFunction}>
            <Stack sx={{ p: 3 }}>
                <Typography variant="h6">Novo Endereço</Typography>
            </Stack>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} sx={{ p: 3 }}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    {...register('IN_PADRAO')}
                                    onChange={(e) => setValue('IN_PADRAO', e.target.checked)}
                                />
                            }
                            label="Endereço Padrão"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Nome do Endereço"
                            fullWidth
                            {...register('NM_ENDERECO')}
                            error={!!errors.NM_ENDERECO}
                            helperText={errors.NM_ENDERECO?.message}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="NR_CEP"
                            control={control}
                            render={({ field }) => (
                                <InputMask
                                    {...field}
                                    mask="99999-999"
                                    onChange={(e) => field.onChange(e.target.value)}
                                >
                                    {(inputProps) => (
                                        <TextField
                                            {...inputProps}
                                            label="CEP"
                                            fullWidth
                                            error={!!errors.NR_CEP}
                                            helperText={errors.NR_CEP?.message}
                                        />
                                    )}
                                </InputMask>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Rua"
                            fullWidth
                            {...register('DS_LOGRADOURO')}
                            error={!!errors.DS_LOGRADOURO}
                            helperText={errors.DS_LOGRADOURO?.message}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Número do Prédio"
                            fullWidth
                            {...register('NR_PREDIO')}
                            error={!!errors.NR_PREDIO}
                            helperText={errors.NR_PREDIO?.message}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Bairro"
                            fullWidth
                            {...register('NM_BAIRRO')}
                            error={!!errors.NM_BAIRRO}
                            helperText={errors.NM_BAIRRO?.message}
                        />
                    </Grid>
                    <Grid item xs={12} md={8}>
                        <TextField
                            label="Cidade"
                            fullWidth
                            {...register('NM_CIDADE')}
                            error={!!errors.NM_CIDADE}
                            helperText={errors.NM_CIDADE?.message}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            label="UF"
                            fullWidth
                            {...register('NM_UF')}
                            error={!!errors.NM_UF}
                            helperText={errors.NM_UF?.message}
                        >
                            {isUFsLoading && <MenuItem disabled>Carregando UFs...</MenuItem>}
                            <MenuItem disabled>Nacionais</MenuItem>
                            {defaultUFs.map((uf) => (
                                <MenuItem key={uf} value={uf}>
                                    {uf}
                                </MenuItem>
                            ))}
                            <MenuItem disabled>Internacionais</MenuItem>
                            {customUFs.map((uf) => (
                                <MenuItem key={uf} value={uf}>
                                    {uf}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Complemento"
                            fullWidth
                            {...register('DS_COMPLEMENTO')}
                            error={!!errors.DS_COMPLEMENTO}
                            helperText={errors.DS_COMPLEMENTO?.message}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Referência"
                            fullWidth
                            {...register('DS_REFERENCIA')}
                            error={!!errors.DS_REFERENCIA}
                            helperText={errors.DS_REFERENCIA?.message}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" justifyContent="space-between">
                            <Button variant="outlined" onClick={closeModelFunction}>
                                Cancelar
                            </Button>
                            <Button 
                                type="submit" 
                                variant="contained" 
                                sx={{ backgroundColor: 'black', '&:hover': { backgroundColor: '#333' } }}
                            >
                                Salvar Endereço
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </form>
        </Dialog>
    );
}
